import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore'

import store from '@/store'
import Materials from '@/js/Materials'
import { db } from '@/js/db'

// The worktop code for a slot, tolerating a bare string value.
function worktopCode(slot) {
  if (!slot) return null
  if (typeof slot === 'string') return slot
  return slot.material || null
}

function regionMatch(worktop, region) {
  if (!region) return true
  const regions = worktop.regions || (worktop.region ? [worktop.region] : [])
  return regions.includes(region)
}

export function resolveWorktop(slot, region) {
  if (!slot) return null

  if (typeof slot !== 'string' && slot.worktopId) {
    const fromLive = store.state.worktops?.find((w) => w.id === slot.worktopId)
    if (fromLive) return fromLive
    return store.state.worktopsByIdCache?.[slot.worktopId] || null
  }

  const code = worktopCode(slot)
  if (code) {
    const fromLive = store.state.worktops?.find((w) => w.value === code && regionMatch(w, region))
    if (fromLive) return fromLive
    return store.state.latestArchivedWorktopByValueCache?.[code] || null
  }

  return null
}

export function getSupersedingLiveWorktop(slot, region) {
  if (!slot || typeof slot === 'string' || !slot.worktopId) return null
  const resolved = resolveWorktop(slot, region)
  if (!resolved || !resolved.archived) return null
  const live = store.state.worktops?.find((w) => w.value === resolved.value && regionMatch(w, region))
  return live && live.id !== resolved.id ? live : null
}

export function isWorktopDiscontinued(slot, region) {
  const code = worktopCode(slot)
  if (!code && !(slot && slot.worktopId)) return false
  const resolved = resolveWorktop(slot, region)
  if (!resolved) return true
  if (!resolved.archived) return false
  return !store.state.worktops?.some((w) => w.value === resolved.value && regionMatch(w, region))
}

export function isColourDiscontinued(slot, region) {
  if (!slot || typeof slot === 'string' || !slot.colourCode) return false
  const matValue = slot.colourType || resolveWorktop(slot, region)?.material
  if (!matValue) return false
  const liveMat = store.state.materials?.find((m) => m.value === matValue)
  if (!liveMat) return true
  return !liveMat.colours?.some((c) => c.value === slot.colourCode)
}

export function resolveWorktopColour(slot, region) {
  if (!slot || typeof slot === 'string' || !slot.colourCode) return null
  const matValue = slot.colourType || resolveWorktop(slot, region)?.material
  let colour = null
  if (matValue) {
    const palette = Materials.getColoursFromValue(matValue) || []
    colour = palette.find((c) => c.value === slot.colourCode) || null
  }
  return {
    code: slot.colourCode,
    name: slot.colourName || colour?.text || slot.colourCode,
    hex: slot.colourHex || colour?.hex || null
  }
}

async function ensureWorktopByIdLoaded(id) {
  if (!id) return
  if (store.state.worktops?.some((w) => w.id === id)) return
  if (store.state.worktopsByIdCache?.[id]) return
  const snap = await getDoc(doc(db, 'worktops', id))
  if (!snap.exists()) return
  store.commit('cacheWorktopById', { ...snap.data(), id: snap.id })
}

async function ensureLatestArchivedWorktopByValueLoaded(value) {
  if (!value) return
  if (store.state.worktops?.some((w) => w.value === value)) return
  if (store.state.latestArchivedWorktopByValueCache?.[value]) return
  const snap = await getDocs(
    query(collection(db, 'worktops'), where('value', '==', value), where('archived', '==', true))
  )
  let latest = null
  snap.forEach((d) => {
    const data = d.data()
    if (!data.dateArchived) return
    if (!latest || data.dateArchived.seconds > latest.dateArchived.seconds) {
      latest = { ...data, id: d.id }
    }
  })
  if (latest) {
    store.commit('cacheLatestArchivedWorktopByValue', { value, worktop: latest })
  }
}

export async function prefetchWorktopsForVersion(version) {
  if (!version || !version.worktops) return
  const tasks = []
  for (const slot of version.worktops) {
    if (slot.worktopId) {
      tasks.push(ensureWorktopByIdLoaded(slot.worktopId))
    } else if (slot.material) {
      tasks.push(ensureLatestArchivedWorktopByValueLoaded(slot.material))
    }
  }
  await Promise.all(tasks)
}

export function pinWorktopIdsOnVersion(version, region) {
  if (!version || !version.worktops) return
  for (const slot of version.worktops) {
    if (slot.worktopId) continue
    if (!slot.material) continue
    const resolved = resolveWorktop(slot, region)
    if (resolved && resolved.id) slot.worktopId = resolved.id
  }
}

export async function convertOnReadForOrder(version, region, orderCreatedAt) {
  return convertOnReadAt(version, region, orderCreatedAt)
}

export async function convertOnReadForPublished(version, region, publishedAt) {
  return convertOnReadAt(version, region, publishedAt)
}

async function convertOnReadAt(version, region, atTimestamp) {
  if (!version || !version.worktops) return false
  let dirty = false
  for (const slot of version.worktops) {
    if (slot.worktopId) continue
    if (!slot.material) continue
    const resolved = await resolveWorktopSlotAtTimestamp(slot.material, region, atTimestamp)
    if (resolved && resolved.id) {
      slot.worktopId = resolved.id
      dirty = true
    }
  }
  return dirty
}

async function resolveWorktopSlotAtTimestamp(value, region, createdAt) {
  const archivedSnap = await getDocs(
    query(collection(db, 'worktops'), where('value', '==', value), where('archived', '==', true))
  )
  const archivedDocs = archivedSnap.docs.map((d) => ({ ...d.data(), id: d.id }))

  const createdSeconds = createdAt && createdAt.seconds
  if (createdSeconds) {
    const match = archivedDocs.find((w) => {
      if (!w.dateArchived) return false
      const archivedSeconds = w.dateArchived.seconds
      const prevSeconds = w.prevArchivedDate ? w.prevArchivedDate.seconds : null
      const aboveLowerBound = prevSeconds === null ? true : createdSeconds > prevSeconds
      const atOrBelowUpperBound = createdSeconds <= archivedSeconds
      return aboveLowerBound && atOrBelowUpperBound
    })
    if (match) {
      store.commit('cacheWorktopById', match)
      return match
    }
  }

  // 2. Current live doc (region-scoped).
  const live = store.state.worktops?.find((w) => w.value === value && regionMatch(w, region))
  if (live) return live

  // 3. Latest archived as last resort.
  let latest = null
  for (const w of archivedDocs) {
    if (!w.dateArchived) continue
    if (!latest || w.dateArchived.seconds > latest.dateArchived.seconds) {
      latest = w
    }
  }
  if (latest) {
    store.commit('cacheWorktopById', latest)
    store.commit('cacheLatestArchivedWorktopByValue', { value, worktop: latest })
    return latest
  }
  return null
}
